import { updateBusinessEntity } from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Label,
  LoadingIndicator,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToggle,
} from '@commenda/kakashi';
import {
  IBusinessEntity,
  TaxClassificationEntityTypeEnum,
} from '@commenda/types';
import { TAX_CLASSIFICATION_ENTITY_TYPE_ENUM_TO_TEXT } from '@commenda/utils';
import { useEffect, useState } from 'react';
import { useNotification } from '../../../../../hooks/useNotification';

const EditTaxClassificationModal = ({
  isOpen,
  onClose,
  businessEntity,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  businessEntity: IBusinessEntity;
  onSuccess: () => void;
}) => {
  const isUpdatingTaxClassification = useToggle();
  const { setNotification } = useNotification();

  const [taxClassification, setTaxClassification] =
    useState<TaxClassificationEntityTypeEnum>(undefined);

  const close = () => {
    setTaxClassification(undefined);
    onClose();
  };

  useEffect(() => {
    if (!businessEntity) return;

    setTaxClassification(
      businessEntity?.onboardingInfo?.countrySpecificFields?.US?.taxedAs
    );
  }, [businessEntity]);

  const isEditing =
    !!businessEntity?.onboardingInfo?.countrySpecificFields?.US?.taxedAs;

  const onSave = async () => {
    if (!businessEntity) return;

    if (!taxClassification) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Tax classification is required.',
      });
      return;
    }

    isUpdatingTaxClassification.on();

    const res = await updateBusinessEntity(businessEntity.id, {
      taxedAs: taxClassification,
    });

    if (!res.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to update tax classification.',
      });
      isUpdatingTaxClassification.off();
      return;
    }

    setNotification({
      show: true,
      valid: true,
      title: 'Success',
      message: 'Tax classification updated successfully.',
    });
    onSuccess();
    close();
    setTaxClassification(undefined);
    isUpdatingTaxClassification.off();
  };

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent preventCloseOnOutsideClick>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Tax Classification' : 'Add Tax Classification'}
          </DialogTitle>
        </DialogHeader>
        <div className="p-3xl">
          <FieldWrapper>
            <Label>Tax Classification</Label>
            <Select
              value={taxClassification}
              onValueChange={(value: TaxClassificationEntityTypeEnum) =>
                setTaxClassification(value)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Tax Classification" />
              </SelectTrigger>
              <SelectContent>
                {Object.keys(TaxClassificationEntityTypeEnum).map(
                  (txClassification) => (
                    <SelectItem key={txClassification} value={txClassification}>
                      {
                        TAX_CLASSIFICATION_ENTITY_TYPE_ENUM_TO_TEXT[
                          txClassification as TaxClassificationEntityTypeEnum
                        ]
                      }
                    </SelectItem>
                  )
                )}
              </SelectContent>
            </Select>
          </FieldWrapper>
        </div>
        <DialogFooter>
          <Button variant="secondary" size="sm" onClick={close} type="button">
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={onSave}
            disabled={isUpdatingTaxClassification.visible || !taxClassification}
          >
            {isUpdatingTaxClassification.visible ? (
              <LoadingIndicator size={16} />
            ) : null}
            <span>Save</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { EditTaxClassificationModal };
